import React, { Suspense, useEffect } from 'react'
import { lazy } from '@loadable/component'
import { ConnectedRouter } from 'connected-react-router'
import history from '../history'
import config from '../Config'
import { Route, Switch } from 'react-router-dom'
import PrivateRoute from '../../Common/Components/PrivateRoute'
import { useTour } from '@reactour/tour'
import Loader from '../../Common/Components/Loader'

// Redux
import { request as resourcesRequest } from '../../Resources/Redux/Resources'
import { request as resourcesTypesRequest } from '../../Resources/Redux/ResourcesTypes'
import { request as resourcesJobsRequest } from '../../Resources/Redux/ResourcesJobs'
// Permissions
import {
  VehiclesPermissions,
  DevicesPermissions,
  UsersPermissions,
  ResourcesPermissions,
  ConfiguratorPermissions,
  DashboardPermissions,
  DeviceImagesArchivePermissions,
  DataPermissions,
  ReportPermissions,
  BlueTagPermissions,
  FrecPermissions,
  FtaoPermissions,
  DigitalTwinPermissions,
  TestCellsPermissions,
} from '../../Auth/Permissions'
import Config from '../Config'
// Views
const LoginView = lazy(() => import('../../Auth/Views/LoginView'))
const SupportView = lazy(() => import('../../Core/Views/SupportView'))
const DashboardView = lazy(() => import('../Views/DashboardView'))
const ForbiddenView = lazy(() => import('../Views/ForbiddenView'))
const ServerErrorView = lazy(() => import('../Views/ServerErrorView'))
const PageNotFoundView = lazy(() => import('../Views/PageNotFoundView'))
// Apps
const VehiclesApp = lazy(() => import('../../Vehicles'))
const DevicesApp = lazy(() => import('../../Devices'))
const AuthApp = lazy(() => import('../../Auth'))
const ResourcesApp = lazy(() => import('../../Resources'))
const ConfiguratorApp = lazy(() => import('../../Configurator'))
const ArchiveApp = lazy(() => import('../../Archive'))
const MaintenanceView = lazy(() => import('../Views/MaintenanceView'))
const FrecApp = lazy(() => import('../../FRec'))
const FtaoApp = lazy(() => import('../../Ftao'))
const DigitalTwinApp = lazy(() => import('../../DigitalTwin'))
const TestCellsApp = lazy(() => import('../../TestCells'))
// Routers
const DataAnalyticsRouter = lazy(() => import('../../Data/Router/DataAnalyticsRouter'))
const ReportsRouter = lazy(() => import('../../Reports/Router'))
const BlueTagRouter = lazy(() => import('../../BlueTag/Router'))

const EMPTY_ARRAY = []

/**
 * This component defines all the application routes, public and private ones.
 * Implement the right shouldDispatch functions!
 */
const AppRouter = () => {
  const { setSteps } = useTour()
  useEffect(() => {
    const fn = history.listen(() => {
      // reset tour, then each view can set its own
      setSteps(EMPTY_ARRAY)
    })

    return () => {
      fn()
    }
  }, [])

  return (
    <ConnectedRouter history={history}>
      <Suspense fallback={<Loader overlay />}>
        <Switch>
          <Route exact path={config.urls.maintenance} component={MaintenanceView} />
          <Route exact path={config.urls.login} component={LoginView} />
          <Route exact path={config.urls.serverError} component={ServerErrorView} />
          <Route exact path={config.urls.support} component={SupportView} />
          <Route exact path={config.urls.forbidden} component={ForbiddenView} />
          <PrivateRoute
            path={config.urls.home}
            key="dashboard"
            component={DashboardView}
            permFunction={DashboardPermissions.read}
          />
          <PrivateRoute
            path={config.urls.vehicles.list}
            key="vehicles"
            component={VehiclesApp}
            permFunction={VehiclesPermissions.read}
          />
          <PrivateRoute
            path={config.urls.devices.list}
            key="devices"
            component={DevicesApp}
            permFunction={DevicesPermissions.read}
          />
          <PrivateRoute
            path={config.urls.testCells.list}
            key="devices"
            component={TestCellsApp}
            permFunction={TestCellsPermissions.read}
          />
          <PrivateRoute
            path={config.urls.archive.base}
            key="archive"
            component={ArchiveApp}
            actions={[]}
            permFunction={DeviceImagesArchivePermissions.read}
          />
          <PrivateRoute
            path={config.urls.users}
            key="users"
            component={AuthApp}
            actions={[]}
            permFunction={UsersPermissions.read}
          />
          <PrivateRoute
            path={config.urls.resources}
            key="resources"
            component={ResourcesApp}
            actions={[
              [resourcesRequest(), (state) => !state.resources.fetched],
              [resourcesTypesRequest(), (state) => !state.resourcesTypes.fetched],
              [resourcesJobsRequest(), (state) => !state.resourcesJobs.fetched],
            ]}
            permFunction={ResourcesPermissions.read}
          />
          <PrivateRoute
            path={`${config.urls.configurator}/:id?`}
            key="configurator"
            component={ConfiguratorApp}
            permFunction={ConfiguratorPermissions.read}
          />
          <PrivateRoute
            path={`${config.urls.dataAnalytics.main}`}
            key="dataAnalytics"
            component={DataAnalyticsRouter}
            permFunction={DataPermissions.dataAnalytics.read}
          />
          <PrivateRoute
            path={`${config.urls.reports.main}`}
            key="reports"
            component={ReportsRouter}
            permFunction={ReportPermissions.read}
          />
          <PrivateRoute
            path={`${config.urls.blueTag.main}`}
            key="blueTag"
            component={BlueTagRouter}
            permFunction={BlueTagPermissions.read}
          />
          {Config.frec.enabled && (
            <PrivateRoute
              path={`${config.urls.frec.main}`}
              key="frec"
              component={FrecApp}
              permFunction={FrecPermissions.access}
            />
          )}
          <PrivateRoute
            path={`${config.urls.ftao.main}`}
            key="ftao"
            component={FtaoApp}
            permFunction={FtaoPermissions.access}
          />
          <PrivateRoute
            path={`${config.urls.digitalTwin.main}`}
            key="dt"
            component={DigitalTwinApp}
            permFunction={DigitalTwinPermissions.access}
          />
          <Route path={''} key="404" component={PageNotFoundView} />
        </Switch>
      </Suspense>
    </ConnectedRouter>
  )
}

export default AppRouter
